import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ClientsAdminApi } from '../adminApi.js'
import type { AdminClientSummary } from '../index.js'
import { GRANTABLE_SCOPES } from '../../core/index.js'
import { Save } from 'lucide-react'
import { Button } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = ClientsAdminApi

const GRANTABLE: readonly string[] = GRANTABLE_SCOPES.map(({ scope }) => scope)

function sameScopes(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((scope) => b.includes(scope))
}

/**
 * What a client's tokens may do, editable after onboarding. Saving replaces
 * the whole list, so a scope this screen does not offer (granted some other
 * way) is carried through untouched rather than dropped. A change applies
 * to the next token the client is issued, not to tokens already in use.
 */
export function ClientScopes({ api, client }: { api: AdminApi; client: AdminClientSummary }) {
  const queryClient = useQueryClient()
  const current = client.scopes ?? []
  const [draft, setDraft] = useState<readonly string[] | null>(null)
  const selected = draft ?? current

  const mutation = useMutation({
    mutationFn: (scopes: string[]) => api.updateScopes(client.registrationId, scopes),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      setDraft(null)
    },
  })

  if (client.scopes === undefined) {
    return <div className="text-xs text-ink-mid">This API version does not report a client's scopes.</div>
  }

  const otherScopes = current.filter((scope) => !GRANTABLE.includes(scope))
  const isDirty = !sameScopes(selected, current)

  function toggle(scope: string): void {
    setDraft(selected.includes(scope) ? selected.filter((candidate) => candidate !== scope) : [...selected, scope])
  }

  return (
    <div className="border border-marine/15 bg-white p-4">
      <div className="space-y-2">
        {GRANTABLE_SCOPES.map(({ scope, description }) => (
          <label key={scope} className="flex items-start gap-2 text-xs text-ink cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={selected.includes(scope)} disabled={mutation.isPending} onChange={() => toggle(scope)} />
            <span>
              <span className="font-mono font-bold">{scope}</span>
              <span className="block text-[11px] text-ink-mid">{description}</span>
            </span>
          </label>
        ))}
      </div>
      {otherScopes.length > 0 && (
        <p className="text-[11px] text-ink-mid mt-2">
          Also granted, and kept as is: <span className="font-mono">{otherScopes.join(', ')}</span>
        </p>
      )}
      {selected.length === 0 && <p className="text-[11px] font-bold text-coral mt-2">A client needs at least one scope.</p>}
      {mutation.isError && (
        <p role="alert" className="text-[11px] font-bold text-coral mt-2">
          {mutation.error.message}
        </p>
      )}
      {isDirty && (
        <div className="flex items-center gap-3 mt-3">
          <Button icon={Save} size="sm" disabled={selected.length === 0 || mutation.isPending} onClick={() => mutation.mutate([...selected])}>
            {mutation.isPending ? 'Saving…' : 'Save scopes'}
          </Button>
          <button type="button" disabled={mutation.isPending} onClick={() => setDraft(null)} className="text-xs font-bold text-ink-mid hover:text-ink">
            Cancel
          </button>
          <span className="text-[11px] text-ink-mid">Applies to the next token this client is issued.</span>
        </div>
      )}
    </div>
  )
}
